import { Button, Modal, ModalBody, ModalContent, ModalFooter, ModalHeader } from '@ds';
import { IoClose } from 'react-icons/io5';
import { MdOutlineSaveAlt } from 'react-icons/md';
import JSZip from 'jszip';
import { toast } from 'react-toastify';
import { useState } from 'react';
import { InvoicesSectionTable } from '@features/components/CaseOverview/components';
import { useGetInvoice } from '@/queries/quotes';
import { Invoice } from '@/api/apis/case';
import { Uuid } from '@/api/types';
import { retrieveUnifiedError } from '@/api/utils';

type InvoiceModalProps = {
  title?: string;
  isOpen: boolean;
  size?: 'md' | 'xs' | 'sm' | 'lg' | 'xl' | '2xl' | '3xl' | '4xl' | '5xl' | 'full';
  invoices: Invoice[];
  caseId: Uuid;
  onClose: () => void;
};

export const InvoiceModal = ({
  title = 'Invoices',
  isOpen,
  size = 'xl',
  invoices,
  caseId,
  onClose,
}: InvoiceModalProps) => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const { mutateAsync: getInvoice } = useGetInvoice();

  const onDownloadAllInvoices = async () => {
    try {
      setIsLoading(true);
      const zip = new JSZip();

      const invoicePromises = invoices.map(async (invoice) => {
        const blob = await getInvoice({
          caseId,
          documentId: invoice.id,
        });

        const sanitizedFileName = invoice.fileName.replace(/\//g, ' ∕ ');

        zip.file(sanitizedFileName, blob, { binary: true });
      });

      await Promise.all(invoicePromises);

      const zipBlob = await zip.generateAsync({ type: 'blob' });

      const url = URL.createObjectURL(zipBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `invoices_${caseId}.zip`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      setTimeout(() => URL.revokeObjectURL(url), 100);
    } catch (e) {
      toast.error(retrieveUnifiedError(e).message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Modal
      size={size}
      isOpen={isOpen}
      onClose={onClose}
      backdrop="opaque"
      closeButton={<IoClose size={32} className="z-1000" />}
      classNames={{
        base: 'px-6 max-w-(--breakpoint-sm) rounded-3xl',
        body: 'border-t-1 border-base-40 px-0 pt-6 pb-0 text-l-r',
        header: 'px-0 pt-6 pb-5 font-secondary text-hl! text-grey-20',
        footer: 'px-0 pt-[18px] pb-6 flex gap-6',
        closeButton: 'w-8 h-8 top-8 right-4 p-0',
      }}
    >
      <ModalContent>
        <>
          <ModalHeader>{title}</ModalHeader>
          <ModalBody>
            <InvoicesSectionTable
              invoiceButtonVariant="md"
              wrapperClassName="border-2 border-base-70"
              additionalBaseClassName="rounded-lg border-0"
              invoices={invoices}
              caseId={caseId}
            />
          </ModalBody>
          <ModalFooter>
            <Button
              size="sm"
              className="gap-1"
              endContent={<MdOutlineSaveAlt />}
              onPress={onDownloadAllInvoices}
              isLoading={isLoading}
              isDisabled={isLoading}
            >
              Download All
            </Button>
          </ModalFooter>
        </>
      </ModalContent>
    </Modal>
  );
};
